import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Box,
  Badge,
} from "@material-ui/core";
import { useForceUpdate } from "../../../lib";
import { useSelector } from "react-redux";
import { useFormikContext, Field } from "formik";
import { TextField, CheckboxWithLabel } from "formik-material-ui";
import { WatchActions, WatchSelectors } from "../../../state";
import { DebouncedTextField } from "./DebouncedTextField";

interface Props {
  classes: any;
  setFieldValue: any;
  activeStep: number;
  setActiveStep: any;
  requiredBrandBadge: any;
  setRequiredBrandBadge: any;
  setOpenAccordion: any;
  openAccordion: any;
  watchId: number;
  isEdit: boolean;
}

export const WatchModel = (props: Props) => {
  const selectStyles = {
    fontSize: '12px',
    height: '24px',
    padding: '2px 12px 2px 5px',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    // borderRadius: '4px',
    backgroundColor: 'transparent',
    width: '100%',
  };
  let forceUpdate = useForceUpdate();
  const { values: _values, initialValues: _initialValues } = useFormikContext();
  const siteId = useSelector((state: any) => state.scopes.site_id);
  const values: any = _values;
  const initialValues: any = _initialValues;
  const {
    classes,
    setFieldValue,
    requiredBrandBadge,
    setRequiredBrandBadge,
  } = props;
  const {
    brand: brand_list,
    size: size_list,
    series: series_list,
    series_group: series_group_list = [],
  } = useSelector(WatchSelectors.watches);
  const [regex, setRegex] = useState(
    '{"flags":"g","source":"[\\\\/\\\\\\\\\\\\-\\\\._\\\\s]"}',
  );
  const [prefix, setPrefix] = useState("");
  const [suffix, setSuffix] = useState("");
  useEffect(() => {
    if (values.brand_id) {
      (async function () {
        const data = await WatchActions.getRegex(values.brand_id);
        if (data?.aka_regex) setRegex(data.aka_regex);
        setPrefix(data?.prefix || "");
        setSuffix(data?.suffix || "");
        if (values?.model_no) {
          setFieldValue(
            "model_no_alphanum",
            ((data?.prefix || "") +
              values.model_no?.replace(
                new RegExp(data?.aka_regex.source, data?.aka_regex.flags),
                "",
              ) +
              (data?.suffix || "") || "").replace(/[^a-zA-Z0-9]/g, ""),
          );
        }
      })();

      forceUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.brand_id]);
  useEffect(() => {
    if (!values.is_special_edition) {
      setFieldValue("special_edition_notes", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.is_special_edition]);
  useEffect(() => {
    if (!values.is_limited) {
      setFieldValue("limited_qty", null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.is_limited]);
  // When editing an existing watch, series_group_id isn't on the item record —
  // derive it from the series list once it's loaded.
  useEffect(() => {
    if (values.series_id && !values.series_group_id && series_list.length) {
      const match = series_list.find((s: any) => s.series_id === values.series_id);
      if (match?.series_group_id) {
        setFieldValue("series_group_id", match.series_group_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.series_id, series_list]);
  useEffect(() => {
    if (!regex) return;

    const obj = JSON.parse(regex);
    if (values?.model_no) {
      setFieldValue(
        "model_no_alphanum",
        (prefix +
          values?.model_no?.replace(new RegExp(obj.source, obj.flags), "") ||
          "").replace(/[^a-zA-Z0-9]/g, "") +
        suffix,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.brand_id, values.model_no]);
  // Baseline of the stub trigger fields captured on the FIRST fire of the
  // useEffect below. Subsequent fires compare against this and only regen the
  // stub when brand_id/series_id/model_no/model_no_2 differ from what they
  // were at first render. This prevents the auto-regen from silently
  // overwriting the router-live `seo_stub` on initial load when the admin
  // hasn't touched anything — the bug that made the admin field disagree
  // with what the storefront actually serves.
  const stubTriggerRef = useRef<{ [k: string]: any } | null>(null);
  useEffect(() => {
    if (!brand_list?.length) return;

    // Title regen: still happens on every relevant change (title is not
    // SEO-URL-critical the way stub is). Kept in-line with the stub check
    // to preserve the existing side-effect timing.
    setFieldValue(
      "title",
      `${values.model_no || ""}${
        values.model_no_2 ? " " + values.model_no_2 : ""
      } ${
        brand_list?.find((x: any) => x?.brand_id === values?.brand_id)
          ?.brand || ""
      } ${
        series_list?.find((x: any) => x?.series_id === values?.series_id)
          ?.series || ""
      } ${
        size_list?.find((x: any) => x?.size_id === values?.size_id)?.size ||
        ""
      } Watch`.trim(),
    );

    // Stub regen — only when a stub-trigger field actually changed from the
    // first-render baseline. Formula: brand + series + model_no + model_no_2
    // + model_no_1. AOMN 1 was added 2026-07-06 as part of the canonical URL
    // migration — every item now has a fully deterministic stub matching the
    // DB-side generator. Size is NOT in the formula (only stripped out via
    // the mm-regex if it happens to appear naturally in the concatenation).
    const triggers = {
      model_no: values.model_no,
      model_no_1: values.model_no_1,
      model_no_2: values.model_no_2,
      brand_id: values.brand_id,
      series_id: values.series_id,
    };
    if (stubTriggerRef.current === null) {
      // First substantive fire: capture baseline, don't touch the stub.
      // Whatever's in Formik state (loaded from server = the router-live URL)
      // stays intact.
      stubTriggerRef.current = triggers;
      return;
    }
    const changed = Object.keys(triggers).some(
      (k) => (triggers as any)[k] !== (stubTriggerRef.current as any)[k],
    );
    if (!changed) return;
    stubTriggerRef.current = triggers;

    // Same gate as legacy: don't regen for old items that never had a stub
    // (item_id ≤ 26273 with an empty stub used the `item/Brand/Series/model`
    // URL scheme — protected from auto-regen).
    if (!values.item_id || values.item_id > 26273 || values.seo_stub) {
      var seo_stub = `${
        brand_list?.find((x: any) => x?.brand_id === values?.brand_id)?.brand ||
        ""
      } ${
        series_list?.find((x: any) => x?.series_id === values?.series_id)
          ?.series || ""
      } ${values.model_no}`;
      seo_stub += values.model_no_2 ? ` ${values.model_no_2}` : "";
      seo_stub += values.model_no_1 ? ` ${values.model_no_1}` : "";

      if (/^.*( [0-9][0-9]\.[0-9]mm).*$/.test(seo_stub)) {
        var match = /^.*( [0-9][0-9]\.[0-9]mm).*$/.exec(seo_stub);
        var pos = match ? seo_stub.indexOf(match[1]) : seo_stub.length;
        seo_stub = seo_stub.substring(0, pos) + seo_stub.substring(pos + 7);
      }
      setFieldValue("seo_stub", seo_stub.replace(/\W+/g, "-"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.model_no,
    values.model_no_1,
    values.model_no_2,
    values.brand_id,
    values.series_id,
    values.size_id,
    brand_list,
    series_list,
    size_list,
  ]);
  const [errorAlert, setErrorAlert] = useState(false);
  useEffect(() => {
    if (props.activeStep > 0 || requiredBrandBadge) {
      setErrorAlert(true);
      let required = 0;
      if (!values.brand_id) {
        required = required + 1;
      }
      if (!values.series_group_id) {
        required = required + 1;
      }
      if (!values.series_id) {
        required = required + 1;
      }
      if (!values.title && !values.default_title) {
        required = required + 1;
      }
      if (!values.seo_stub) {
        required = required + 1;
      }
      if (!values.size_id) {
        required = required + 1;
      }
      if (!values.model_no) {
        required = required + 1;
      }
      setRequiredBrandBadge(required);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.activeStep,
    values.brand_id,
    values.series_group_id,
    values.model_no,
    values.seo_stub,
    values.series_id,
    values.size_id,
    values.title,
  ]);

  // NOTE: The "was the model number changed?" gate for duplication is computed
  // fresh in AddWatchStepper (trim-insensitive, every render) and the backend
  // re-checks uniqueness against the DB. We intentionally do NOT cache a flag
  // here — the old effect overwrote the original model number with a boolean, so
  // any stray re-fire (e.g. a trailing newline from the multiline field on
  // focus) compared string-vs-boolean, flipped to "changed", and let a
  // duplicate through.

  return (
    <Accordion
      expanded={true}
      style={{ width: "100%" }}
    >
      <AccordionSummary
        style={{ cursor: "default" }}
        aria-controls="panel1a-content"
        id="panel1a-header"
        tabIndex={-1}
      >
        <Typography>
          <Badge badgeContent={props.requiredBrandBadge} color="secondary">
            Brand & Model
          </Badge>
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        className={classes.grid}
        style={{ display: "grid" }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          flexDirection="column"
          alignItems="left"
        >

          {/* ── ROW 1: Brand | Series Group | Series | Size ── */}
          <Box display="flex" alignItems="center" style={{ marginBottom: 8, marginRight: 8, marginLeft: 10, gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", width: "14%", gap: 0 }}>
              <label style={{ fontSize: 11, ...(errorAlert && !values.brand_id ? { color: '#d32f2f' } : {}) }}>Brand:</label>
              <select
                value={values.brand_id || ""}
                onChange={(e) => setFieldValue("brand_id", e.target.value ? Number(e.target.value) : null)}
                disabled={siteId !== 0}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.brand_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : errorAlert
                    ? `${classes.gridTwoRed} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={{ ...selectStyles, ...(errorAlert && !values.brand_id ? { border: '1px solid #d32f2f' } : {}) }}
              >
                <option value="">Brand</option>
                {brand_list
                  .sort((a, b) => (a.brand || "").localeCompare(b.brand || ""))
                  .map((b) => (
                    <option key={b.brand_id} value={b.brand_id}>
                      {b.brand}
                    </option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", width: "8%", gap: 0 }}>
              <label style={{ fontSize: 11, ...(errorAlert && !values.size_id ? { color: '#d32f2f' } : {}) }}>Size:</label>
              <select
                value={values.size_id || ""}
                onChange={(e) => {
                  setFieldValue("size_id", e.target.value ? Number(e.target.value) : null);
                }}
                disabled={siteId !== 0}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.size_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : errorAlert
                    ? `${classes.gridTwoRed} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={{ ...selectStyles, ...(errorAlert && !values.size_id ? { border: '1px solid #d32f2f' } : {}) }}
              >
                <option value="">Size</option>
                {size_list
                  .sort((a: any, b: any) => (a.size || "").localeCompare(b.size || ""))
                  .map((s: any) => (
                    <option key={s.size_id} value={s.size_id}>
                      {s.size}
                    </option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", width: 320, gap: 0 }}>
              <label style={{ fontSize: 11, ...(errorAlert && !values.series_group_id ? { color: '#d32f2f' } : {}) }}>Series Group:</label>
              <select
                value={values.series_group_id || ""}
                onChange={(e) => {
                  setFieldValue("series_group_id", e.target.value ? Number(e.target.value) : null);
                  setFieldValue("series_id", null);
                }}
                disabled={siteId !== 0 || !values.brand_id}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.series_group_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : errorAlert
                    ? `${classes.gridTwoRed} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={{ ...selectStyles, ...(errorAlert && !values.series_group_id ? { border: '1px solid #d32f2f' } : {}) }}
              >
                <option value="">Series Group</option>
                {series_group_list
                  .filter((g: any) => g.brand_id === values.brand_id)
                  .sort((a: any, b: any) => (a.series_group || "").localeCompare(b.series_group || ""))
                  .map((g: any) => (
                    <option key={g.series_group_id} value={g.series_group_id}>
                      {g.series_group}
                    </option>
                  ))}
              </select>
            </div>
            <div style={{ display: "flex", flexDirection: "column", width: 500, gap: 0 }}>
              <label style={{ fontSize: 11, ...(errorAlert && !values.series_id ? { color: '#d32f2f' } : {}) }}>Series:</label>
              <select
                value={values.series_id || ""}
                onChange={(e) => {
                  setFieldValue("series_id", e.target.value ? Number(e.target.value) : null);
                }}
                disabled={siteId !== 0 || !values.brand_id}
                className={
                  siteId !== 0
                    ? `${classes.gridTwoBlack} custom-select`
                    : values.series_id
                    ? `${classes.gridTwoBlue} custom-select`
                    : errorAlert
                    ? `${classes.gridTwoRed} custom-select`
                    : `${classes.gridTwo} custom-select`
                }
                style={{ ...selectStyles, ...(errorAlert && !values.series_id ? { border: '1px solid #d32f2f' } : {}) }}
              >
                <option value="">Series</option>
                {series_list
                  .filter((s: any) =>
                    s.brand_id === values.brand_id &&
                    (!values.series_group_id || s.series_group_id === values.series_group_id)
                  )
                  .sort((a: any, b: any) => (a.series || "").localeCompare(b.series || ""))
                  .map((s: any) => (
                    <option key={s.series_id} value={s.series_id}>
                      {s.series}
                    </option>
                  ))}
              </select>
            </div>
          </Box>

          {/* ── ROW 2: Strict Model No | Add On 1 | Add On 2 | GTIN | Alphanumeric (display) | AKA ── */}
          <Box display="flex" alignItems="center" style={{ marginBottom: 8, marginRight: 8, marginLeft: 10, gap: 16 }}>
            {/* Strict Model Number — moved here as first item */}
            <div style={{ display: "flex", flexDirection: "column", width: 170, gap: 1 }}>
              <label style={{ fontSize: 11, ...(errorAlert && !values.model_no ? { color: '#d32f2f' } : {}) }}>Strict Model Number:</label>
              <DebouncedTextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : values.model_no
                    ? classes.gridTwoBlue
                    : errorAlert
                    ? classes.gridTwoRed
                    : classes.gridTwo
                }                disabled={siteId !== 0}
                name="model_no"
                type="text"
                style={{ width: "100%" }}
                multiline
              />
            </div>

            {/* Add On Model Number 1 */}
            <div style={{ display: "flex", flexDirection: "column", width: 200, gap: 1 }}>
              <label style={{ fontSize: 11 }}>Add On Model Number 1:</label>
              <DebouncedTextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : values.model_no_1
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }                disabled={siteId !== 0}
                name="model_no_1"
                type="text"
                style={{ width: "100%" }}
                multiline
              />
            </div>

            {/* Add On Model Number 2 */}
            <div style={{ display: "flex", flexDirection: "column", width: 200, gap: 1 }}>
              <label style={{ fontSize: 11 }}>Add On Model Number 2:</label>
              <DebouncedTextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : values.model_no_2
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }                disabled={siteId !== 0}
                name="model_no_2"
                type="text"
                style={{ width: "100%" }}
                multiline
              />
            </div>

            {/* AKA — Put back in */}            
            <div style={{ display: "flex", flexDirection: "column", width: 137, gap: 1 }}>
              <label style={{ fontSize: 11 }}>AKA:</label>
              <DebouncedTextField
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : values.model_aka
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }                disabled={siteId !== 0}
                name="model_aka"
                type="text"
                style={{ width: "100%" }}
                multiline
              />
            </div>

            {/* GTIN */}
            <div style={{ display: "flex", flexDirection: "column", width: 130, gap: 1 }}>
              <label style={{ fontSize: 11 }}>GTIN:</label>
              <Field
                className={
                  siteId !== 0
                    ? classes.gridTwoBlack
                    : values.gtin
                    ? classes.gridTwoBlue
                    : classes.gridTwo
                }                component={TextField}
                name="gtin"
                type="text"
                style={{ width: "100%" }}
                disabled={siteId !== 0}
                multiline
              />
            </div>

            {/* Alphanumeric Model Number — borderless display field */}
            <div style={{ display: "flex", flexDirection: "column", width: 160, gap: 1 }}>
              <label style={{ fontSize: 11 }}>Alphanumeric Model Number:</label>
              <div
                style={{
                  fontSize: 12,
                  padding: "2px 0",
                  minHeight: 24,
                  color: values.model_no_alphanum ? "inherit" : "rgba(0,0,0,0.38)",
                  userSelect: "text",
                  cursor: "text",
                  wordBreak: "break-all",
                }}
              >
                {values.model_no_alphanum || "—"}
              </div>
            </div>
            
          </Box>

          {/* ── ROW 3: Special Edition | Limited | Numbered | Next button ── */}
          <Box display="flex" alignItems="center" style={{ marginBottom: 8, marginRight: 8, marginLeft: 10, gap: 16 }}>
            <Field
              disabled={siteId !== 0}
              component={CheckboxWithLabel}
              type="checkbox"
              indeterminate={false}
              style={{ padding: 0 }}
              name="is_special_edition"
              Label={{
                label: <span style={{ fontSize: 11 }}>S.E.</span>,
                labelPlacement: "top",
                style: { margin: 0, gap: 0 },
              }}
              className={classes.gridTwo}
            />


            <div style={{ display: "flex", flexDirection: "column", width: 480, gap: 1 }}>
              <label style={{ fontSize: 11 }}>Special Edition Notes:</label>
              <Field
                className={siteId !== 0 ? classes.gridTwoBlack : values.special_edition_notes ? classes.gridTwoBlue : classes.gridTwo}                disabled={siteId !== 0}
                component={TextField}
                name="special_edition_notes"
                type="text"
                style={{ width: "100%" }}
                multiline
              />
            </div>
            <Field
              disabled={siteId !== 0}
              component={CheckboxWithLabel}
              type="checkbox"
              indeterminate={false}
              style={{ padding: 0 }}
              name="is_limited"
              Label={{
                label: <span style={{ fontSize: 11 }}>L.E.</span>,
                labelPlacement: "top",
                style: { margin: 0, gap: 0 },
              }}
              className={classes.gridTwo}
            />
            <div style={{ display: "flex", flexDirection: "column", width: 80, gap: 1 }}>
              <label style={{ fontSize: 11 }}>Qty:</label>
              <Field
                className={siteId !== 0 ? classes.gridTwoBlack : values.limited_qty ? classes.gridTwoBlue : classes.gridTwo}                disabled={siteId !== 0}
                component={TextField}
                name="limited_qty"
                type="number"
                style={{ width: "100%" }}
              />
            </div>
            <Field
              disabled={siteId !== 0}
              component={CheckboxWithLabel}
              type="checkbox"
              indeterminate={false}
              style={{ padding: 0 }}
              name="is_numbered"
              Label={{
                label: <span style={{ fontSize: 11 }}>Numbered</span>,
                labelPlacement: "top",
                style: { margin: 0, gap: 0 },
              }}
              className={classes.gridTwo}
            />
          </Box>

          {/* ── ROW 4: SEO checkbox + (Title & Stub when expanded) ── */}
          <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginRight: 8, marginBottom: 8, gap: 16 }}>
  
              <Box display="flex" alignItems="center" justifyContent="start" style={{ marginLeft: 8, flex: 1, gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", width: "auto", gap: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: 555 }}>
                  <label style={{ fontSize: 11, ...(errorAlert && !(values.title || values.default_title) ? { color: '#d32f2f' } : {}) }}>Title:</label>
                    <span style={{ fontSize: 11, color: (values.title || values.default_title || "").length > 60 ? "red" : "inherit" }}>
                      ({(values.title || values.default_title || "").length} characters)
                    </span>
                  </div>
                  <Field
                    className={
                      values.title || values.default_title
                        ? classes.gridTwoBlue
                        : errorAlert
                        ? classes.gridTwoRed
                        : classes.gridTwo
                    }
                    component={TextField}
                    name={values.title ? "title" : "default_title"}
                    style={{ width: 560 }}
                    multiline
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", width: "auto", gap: 1 }}>
                  <label style={{ fontSize: 11, ...(errorAlert && !values.seo_stub ? { color: '#d32f2f' } : {}) }}>Stub:</label>
                  <Field
                    className={
                      values.seo_stub
                        ? classes.gridTwoBlue
                        : errorAlert
                        ? classes.gridTwoRed
                        : classes.gridTwo
                    }                    component={TextField}
                    name="seo_stub"
                    style={{ width: 555 }}
                    multiline

                  />
                  {/* Drift detector — shows the router-live stub from initial load
                      (fetched via getStubsForItems, so it IS the live URL). When
                      auto-regen or a manual edit changes the field, this line
                      shows what will REPLACE what's currently live. Empty on add. */}
                  {initialValues?.seo_stub && initialValues.seo_stub !== values.seo_stub && (
                    <div style={{ fontSize: 10, color: '#b26a00', marginTop: 2, wordBreak: 'break-all' }}>
                      Current in DB: <span style={{ fontFamily: 'monospace' }}>{initialValues.seo_stub}</span>
                    </div>
                  )}
                </div>
              </Box>


          </Box>

        </Box>
      </AccordionDetails>
    </Accordion>
  );
};